import {
  BellAlertIcon,
  ChartBarIcon,
  // TagIcon,
} from "@heroicons/react/24/outline";
import qs from "qs";
import Select from "common/components/Select/Select";
import { useTranslation } from "react-i18next";
import { startOfDay, endOfDay } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import HeaderWithRightCustomSection from "common/components/Header/HeaderWithRightCustomSection";
import Filters from "common/components/Header/Filters";
import SiteSelect from "components/select/SiteSelect/SiteSelect";
import getWording from "common/utils/wording";
import WorkSiteIcon from "components/Icon/icons/WorkSiteIcon";
import PostSelect from "components/select/PostSelect/PostSelect";
import PostTypeSelect from "components/select/AlertTypeSelect/PostTypeSelect";
import DatePicker from "common/components/DatePicker/DatePicker";
import useOrganization from "common/hooks/useOrganization";
import fetchJSON from "common/utils/fetchJSON";
import ThreadTypeSelect from "components/select/ThreadTypeSelect/ThreadTypeSelect";
// import TagSelect from "components/select/TagSelect/TagSelect";
import PageLoading from "common/components/PageLoading/PageLoading";
import PostStatistics from "./PostStatistics";
import ThreadStatistics from "./ThreadStatistics";
import SiteStatistics from "./SiteStatistics";
import OrganizationStatistics from "./OrganizationStatistics";
import UserStatistics from "./UserStatistics";

export default function Stats() {
  const { t } = useTranslation();
  const [statsToDisplay, setStatsToDisplay] = useState<
    "sites" | "posts" | "threads" | "organizations" | "users"
  >("organizations");
  const [filters, setFilters] = useState<any>({});
  const { organization } = useOrganization();

  // Pré-calcul des wordings
  const siteWording = getWording(false, true);
  const sitesWording = getWording(true, true);

  const [data, setData] = useState<any>({});
  const [isFetching, setIsFetching] = useState<boolean>(true);

  // Convert filters into query string
  const buildQueryParams = (f: any) => {
    return qs.stringify(f, {
      encodeValuesOnly: true,
    });
  };

  // Make the url depending on filters and statsToDisplay
  const buildUrl = useMemo(() => {
    if (!statsToDisplay || !organization?.id) return null;

    // Pour les stats de sites, ne faire la requête que si un site spécifique est sélectionné
    if (statsToDisplay === "sites" && !filters.site) {
      return null;
    }

    // Special case for users stats - uses stats/users endpoint
    const baseUrl =
      statsToDisplay === "users" ? "stats/users" : `${statsToDisplay}/stats`;
    const filterParams = buildQueryParams(filters);

    return `${baseUrl}/${organization.id}/${
      filterParams ? `?${filterParams}` : ""
    }`;
  }, [organization, statsToDisplay, filters]);

  useEffect(() => {
    let isCancelled = false;

    if (buildUrl) {
      const getData = async () => {
        setIsFetching(true);
        try {
          const res = await fetchJSON({
            url: buildUrl,
            method: "GET",
          });
          if (!isCancelled) {
            setData(res);
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des données :", error);
        } finally {
          if (!isCancelled) {
            setIsFetching(false);
          }
        }
      };

      getData();
    }

    return () => {
      isCancelled = true; // On signale que cet effet est annulé
    };
  }, [buildUrl]);

  const handleChangeFilter = (filterName: string, value?: string) => {
    if (value && value !== null) {
      setFilters((prev: any) => ({ ...prev, [filterName]: value }));
    } else {
      setFilters((prev: any) => {
        const newFilters = { ...prev };
        delete newFilters[filterName];
        return newFilters;
      });
    }
  };

  // Filters columns
  const columns = useMemo(() => {
    const defaultColumns = [
      {
        Header: t("forms.startDate", {
          wording: siteWording,
        }),
        filterName: "startDate",
        columnClasses: "md:flex-1",
        customFormat: (date: any) => {
          return date ? startOfDay(date) : date;
        },
        filterComponent: (props: any) => (
          <DatePicker showTimeSelect={false} {...props} />
        ),
      },
      {
        Header: t("forms.endDate", {
          wording: siteWording,
        }),
        filterName: "endDate",
        customFormat: (date: any) => {
          return date ? endOfDay(date) : date;
        },
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <DatePicker
            showTimeSelect={false}
            minDate={filters?.startDate}
            {...props}
          />
        ),
      },
      {
        Header: t("stats.selectInterval", {
          wording: siteWording,
        }),
        filterName: "scale",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <Select
            options={[
              { value: "month", label: t("stats.month") },
              { value: "week", label: t("stats.week") },
              { value: "day", label: t("stats.day") },
            ]}
            clearable={false}
            {...props}
          />
        ),
      },
    ];

    switch (statsToDisplay) {
      case "sites":
        return [
          {
            Header: t("forms.site", {
              wording: siteWording,
            }),
            filterName: "site",
            columnClasses: "md:flex-1",
            filterComponent: (props: any) => (
              <SiteSelect
                isFilter
                allowReadOnlyOptions
                Icon={WorkSiteIcon}
                {...props}
              />
            ),
          },
          ...defaultColumns,
        ];

      case "posts":
        return [
          {
            Header: t("item.post", {
              wording: siteWording,
            }),
            filterName: "post",
            columnClasses: "md:flex-1",
            filterComponent: (props: any) => (
              <PostSelect
                isFilter
                filterOnSiteId={filters.site}
                Icon={WorkSiteIcon}
                {...props}
              />
            ),
          },
          {
            Header: t("forms.type"),
            filterName: "type",
            columnClasses: "md:flex-1",
            filterComponent: (props: any) => (
              <PostTypeSelect isFilter Icon={BellAlertIcon} {...props} />
            ),
          },
          {
            Header: t("forms.site", {
              wording: siteWording,
            }),
            filterName: "site",
            columnClasses: "md:flex-1",
            filterComponent: (props: any) => (
              <SiteSelect
                isFilter
                allowReadOnlyOptions
                Icon={WorkSiteIcon}
                {...props}
              />
            ),
          },
          ...defaultColumns,
        ];
      case "threads":
        return [
          {
            Header: t("forms.site", {
              wording: siteWording,
            }),
            filterName: "site",
            columnClasses: "md:flex-1",
            filterComponent: (props: any) => (
              <SiteSelect
                isFilter
                allowReadOnlyOptions
                Icon={WorkSiteIcon}
                {...props}
              />
            ),
          },
          {
            Header: t("titlePages.posts"),
            filterName: "post",
            columnClasses: "md:flex-1",
            filterComponent: (props: any) => (
              <PostSelect
                isFilter
                filterOnSiteId={filters.site}
                Icon={WorkSiteIcon}
                {...props}
              />
            ),
          },
          {
            Header: t("forms.type"),
            filterName: "type",
            columnClasses: "md:flex-1",
            filterComponent: (props: any) => (
              <ThreadTypeSelect isFilter Icon={BellAlertIcon} {...props} />
            ),
          },
          ...defaultColumns,
        ];
      default:
        return defaultColumns;
    }
  }, [filters.site, filters?.startDate, statsToDisplay, t, siteWording]);

  const renderStatsContent = () => {
    switch (statsToDisplay) {
      case "sites":
        return (
          <SiteStatistics
            data={data}
            isDetail={filters.site}
            filters={filters}
          />
        );
      case "posts":
        return (
          <PostStatistics
            data={data}
            isDetail={filters.post}
            startDate={filters.startDate}
            endDate={filters.endDate}
            scale={filters.scale}
          />
        );
      case "threads":
        return (
          <ThreadStatistics
            data={data}
            isDetail={filters.site || filters.post || filters.type}
            startDate={filters.startDate}
            endDate={filters.endDate}
            scale={filters.scale}
          />
        );
      case "users":
        return (
          <UserStatistics
            data={data}
            startDate={filters.startDate}
            endDate={filters.endDate}
            scale={filters.scale}
          />
        );
      default:
        return (
          <OrganizationStatistics
            data={data}
            startDate={filters.startDate}
            endDate={filters.endDate}
            scale={filters.scale}
          />
        );
    }
  };

  const handleChangeStatsToDisplayValue = (
    value: "sites" | "posts" | "threads" | "users"
  ) => {
    if (value === null) {
      setStatsToDisplay("organizations");
    } else {
      setStatsToDisplay(value);
    }
    setFilters({});
  };

  return (
    <div className="py-6 h-full">
      <div className="mx-auto text-sm max-w-full h-full max-main-box px-4 mb-4">
        <HeaderWithRightCustomSection
          title={t("titlePages.stats")}
          subtitle={t("titleLegends.stats")}
          Icon={ChartBarIcon}
          rightSection={
            <Select
              placeholder={t(`stats.selectDataToDisplay`)}
              onChange={handleChangeStatsToDisplayValue}
              options={[
                { value: "sites", label: sitesWording },
                { value: "posts", label: "Publications" },
                { value: "threads", label: "Échanges" },
                { value: "users", label: "Utilisateurs" },
              ]}
              value={statsToDisplay}
            />
          }
        />
        <Filters
          columns={columns}
          filters={filters}
          handleChangeFilter={handleChangeFilter}
        />
        {!filters.startDate && (
          <p className="text-xs text-gray-500 italic mt-1 mb-2">
            Aucune date de début renseignée — les statistiques sont limitées aux 90 derniers jours.
          </p>
        )}
        {isFetching ? (
          <div className="w-full fixed left-72 max-main-box top-thirty-vh">
            <PageLoading customStyles="absolute top-[75px] left-[calc(50%-100px)] w-[200px] rounded-full h-16 mx-auto flex justify-center items-center bg-slate-100/80 z-10" />
          </div>
        ) : (
          renderStatsContent()
        )}
      </div>
    </div>
  );
}
